import { useCallback, useContext, useEffect, useState } from "react"
import { DateTime } from "luxon"

//PrimeReact
import { Dialog } from "primereact/dialog"

// Context
import { UserContext } from "../../../context/userContext"

//styling
import myStyles from "../../../styles/assessments/AssessmentForm.module.css"

//SOAR Helpers
import { LoaderMedium } from "../../../components/Loaders"
import { LogError } from "../../../helpers/logger"

//Assessment components
import {
  assessmentsApiQuestionsGet,
  assessmentsApiCompletePost,
  assessmentsApiCompletedGet,
  assessmentsApiCompletedGetWithStaged,
  assessmentsApiCompleteWithSessionPost,
  assessmentsApiUpdateCompletedPost,
  assessmentsApiStageCompletedUpdatePost,
} from "../assessmentsApi"
import AssessmentFormOutcome from "./AssessmentFormOutcome"
import AssessmentFormQuestionContainer from "./AssessmentFormQuestionContainer"
import AssessmentFormInstruction from "./AssessmentFormInstruction"
import { isMatrixQuestionType } from "../assessmentHelpers"
import elementTypes from "../ManagementForm/FormBuilder/Elements/elementTypes"
import { classNames } from "primereact/utils"

export default function AssessmentForm({
  assessmentId,
  organizationId, //When this is set, it overrides the user's organization for assessment submission
  organizationAlias, //When this is set, it overrides the user's organization for assessment submission
  completedAssessmentId,
  stagedAssessmentId,
  stageCompletedAssessmentUpdates,
  taskId,
  submitSoarSession, //If true, submit completed assessments with session creation to Assessment service
  sessionLocation,
  sessionTemplateId,
  readOnly,
  previewMode, //Simulates a regular form submission, but does not generate confirmed completed assessments
  showForm,
  onCancel,
  onSubmit,
}) {
  const userCtx = useContext(UserContext)

  const [activeCompletedAssessmentId, setActiveCompletedAssessmentId] =
    useState(completedAssessmentId)
  const [disable, setDisable] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [page, setPage] = useState(0)
  const [assessment, setAssessment] = useState(undefined)
  const [answers, setAnswers] = useState(undefined)
  const [finalOutcomes, setFinalOutcomes] = useState([])
  const [finalRemarks, setFinalRemarks] = useState("")
  const [isUserAnswers, setIsUserAnswers] = useState(false)
  const [startDateTime] = useState(DateTime.utc())

  let displayCompletedOutcomes =
    completedAssessmentId &&
    readOnly &&
    (assessment?.displayDisposition ||
      assessment?.displayRemarks ||
      assessment?.displayScore)

  const processAnswerSource = (questions, answers) => {
    let newAnswers = []
    const addAnswer = (question) => {
      let value = null
      let optionValues = []

      if (answers) {
        const questionAnswer = answers.filter(
          (answer) => answer.questionId === question.id,
        )

        if (
          question.type === elementTypes.checkboxElementId ||
          question.type === elementTypes.matrixCheckboxElementId
        ) {
          optionValues = questionAnswer
        } else if (questionAnswer.length > 0) {
          value = questionAnswer[0].answerValue
        }
      } else {
        if (question.type === elementTypes.checkboxElementId) {
          optionValues = []
        } else {
          value = question.defaultValue
        }
      }

      newAnswers.push({
        questionId: question.id,
        type: question.type,
        required: question.required,
        conditionalSource: question.conditionalSource,
        conditionalType: question.conditionalType,
        conditionalValue: question.conditionalValue,
        conditionalDisplay: question.conditionalDisplay,
        conditionalRequired: question.conditionalRequired,
        value: value,
        scoreFromAnswer: question.scoreFromAnswer,
        multiValues: question.options?.map((option) => {
          const optionValue = optionValues.filter(
            (optionValue) => optionValue.questionOptionId === option.id,
          )

          return {
            optionId: option.id,
            value:
              optionValue.length > 0
                ? optionValue[0].answerValue === "true"
                : null,
            text: option.text,
            scoreValue: option.scoreValue,
          }
        }),
      })
    }

    questions.forEach((question) => {
      if (question.matrix) {
        question.matrix.groups.forEach((group) => {
          group.questions.forEach((groupQuestion) => {
            addAnswer(groupQuestion)
          })
        })
      } else if (question.questionGroup) {
        question.questionGroup.questions.forEach((groupQuestion) => {
          if (isMatrixQuestionType(groupQuestion.type)) {
            groupQuestion.questionLabels?.forEach((label) => {
              let matrixQuestion = { ...groupQuestion, id: label.id }
              addAnswer(matrixQuestion)
            })
          } else {
            addAnswer(groupQuestion)
          }
        })
      } else if (question.question) {
        if (isMatrixQuestionType(question.question.type)) {
          question.question.questionLabels?.forEach((label) => {
            let matrixQuestion = { ...question.question, id: label.id }
            addAnswer(matrixQuestion)
          })
        } else {
          addAnswer(question.question)
        }
      }
    })

    setAnswers({ answers: newAnswers })
  }

  const loadCompletedAssessment = useCallback(async () => {
    if (completedAssessmentId) {
      setDisable(true)

      try {
        var result = undefined

        if (stagedAssessmentId) {
          result = await assessmentsApiCompletedGetWithStaged(
            userCtx.tokenAssessments,
            completedAssessmentId,
            stagedAssessmentId,
          )
        } else {
          result = await assessmentsApiCompletedGet(
            userCtx.tokenAssessments,
            completedAssessmentId,
          )
        }

        setAssessment({
          assessmentName: result.assessment.name,
          completedAssessmentId: result.completedAssessment.id,
          score: result.completedAssessment.score,
          displayAnswers: result.assessment.displayAnswers,
          displayAnswerValues: result.assessment.displayAnswerValues,
          displayDisposition: result.assessment.displayDisposition,
          displayQuestions: result.assessment.displayQuestions,
          displayRemarks: result.assessment.displayRemarks,
          displayScore: result.assessment.displayScore,
          questions: result.questions,
          suggestedOutcomes: result.suggestedOutcomes,
          scores: result.scores,
        })

        setFinalOutcomes(
          result.finalOutcomes?.map((outcome) => {
            return { ...outcome, selected: true }
          }),
        )

        setFinalRemarks(result.completedAssessment.remarks)

        processAnswerSource(result.questions, result.answers)

        setDisable(false)
      } catch (error) {
        LogError(
          `Unable to load completed assessment profile for ID ${completedAssessmentId}`,
          error,
        )
        userCtx.setNotificationError(
          "Unable to load completed assessment profile",
        )
        onCancel()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedAssessmentId])

  useEffect(() => {
    loadCompletedAssessment()
  }, [loadCompletedAssessment])

  const loadAssessmentProfile = useCallback(async () => {
    if (assessmentId && !completedAssessmentId) {
      setDisable(true)
      assessmentsApiQuestionsGet(userCtx.tokenAssessments, assessmentId)
        .then((result) => {
          setAssessment(result)
          processAnswerSource(result.questions)
          setDisable(false)
        })
        .catch((error) => {
          LogError(
            `Unable to load assessment profile for ID ${assessmentId}`,
            error,
          )
          userCtx.setNotificationError("Unable to load assessment profile")
          onCancel()
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId])

  useEffect(() => {
    loadAssessmentProfile()
  }, [loadAssessmentProfile])

  const handleAssessmentSubmit = async (
    finalAnswers,
    finalOutcomes,
    remarks,
    isFinalSubmit,
  ) => {
    try {
      setDisable(true)
      setSubmitting(true)
      const singleValueAnswers = finalAnswers.answers
        .filter((answer) => answer.value)
        .map((answer) => {
          return {
            questionId: answer.questionId,
            answerValue: answer.value.toString(),
          }
        })

      let multiValueAnswers = []
      finalAnswers.answers
        .filter(
          (answer) =>
            !answer.value &&
            answer.multiValues?.some((multiValue) => multiValue.value),
        )
        .forEach((answer) => {
          multiValueAnswers.push(
            ...answer.multiValues
              .filter((multiValue) => multiValue.value)
              .map((multiValue) => {
                return {
                  questionId: answer.questionId,
                  optionId: multiValue.optionId,
                  answerValue: multiValue.value.toString(),
                }
              }),
          )
        })

      let useFinalOutcomes = finalOutcomes.filter((outcome) => outcome.selected)
      const completedDate = DateTime.now().toISODate() //The completed date should be the selected date only value.

      let payload = {
        assessmentId: assessmentId,
        assignmentTaskId: taskId,
        remarks: remarks,
        staffId: userCtx.session.user.staff_id,
        staffAlias: userCtx.session.user.staff_alias,
        participantId: userCtx.participant_id,
        participantAlias: userCtx.participant_alias,
        enterpriseId: userCtx.session.user.enterprise_id,
        enterpriseAlias: userCtx.session.user.enterprise_alias,
        organizationId: organizationId ?? userCtx.session.user.organization_id,
        organizationAlias:
          organizationAlias ?? userCtx.session.user.organization_alias,
        SoarTemplateId: sessionTemplateId || "ad_hoc_assessment",
        completedDate: completedDate,
        creatorId: userCtx.session.user.staff_id,
        answers: [...singleValueAnswers, ...multiValueAnswers],
        finalOutcomes: useFinalOutcomes,
        isPreviewSubmission: previewMode,
      }

      let result = undefined

      if (activeCompletedAssessmentId) {
        if (stageCompletedAssessmentUpdates) {
          result = await assessmentsApiStageCompletedUpdatePost(
            userCtx.tokenAssessments,
            activeCompletedAssessmentId,
            payload,
          )
        } else {
          result = await assessmentsApiUpdateCompletedPost(
            userCtx.tokenAssessments,
            activeCompletedAssessmentId,
            payload,
          )
        }
      } else {
        if (submitSoarSession && isFinalSubmit) {
          const sessionPayload = {
            completedAssessmentId: activeCompletedAssessmentId, //Send this in to trigger an update instead of create if not undefined
            completedAssessment: payload,
            startDateTime: startDateTime.toISO(),
            endDateTime: DateTime.utc().toISO(),
            locationId: sessionLocation?.location_id,
            locationName: sessionLocation?.location_name,
          }

          result = await assessmentsApiCompleteWithSessionPost(
            userCtx.tokenAssessments,
            sessionPayload,
          )
        } else {
          result = await assessmentsApiCompletePost(
            userCtx.tokenAssessments,
            payload,
          )
        }
      }

      if (result) {
        setDisable(false)
        setSubmitting(false)

        //Save the calculated assessment score
        let newAssessment = {
          ...assessment,
          completedAssessmentId:
            result?.completedAssessment?.id ?? result.completedAssessmentId,
          score: result.completedAssessment?.score ?? result.score,
        }

        if (previewMode) {
          newAssessment.scores = result.scores
          newAssessment.displayScore = true
        }

        setAssessment(newAssessment)

        //Set the completed Assessment Id if there is none. This should only happen after submitting answers the first time.
        const newCompletedAssessmentId =
          result.completedAssessment?.id ?? result.completedAssessmentId
        if (!activeCompletedAssessmentId && newCompletedAssessmentId) {
          setActiveCompletedAssessmentId(newCompletedAssessmentId)
        }

        if (isFinalSubmit) {
          if (result.stagedId) {
            onSubmit({
              completedAssessmentId: result.completedAssessmentId,
              stagedAssessmentId: result.stagedId,
              score: newAssessment.score,
            })
          } else {
            onSubmit({
              completedAssessmentId: result.completedAssessment.id,
              score: newAssessment.score,
            })
          }
        }

        return result
      } else {
        LogError("Unable to save assessment, result is: ", result)
        userCtx.setNotificationError("Unable to save assessment")
        setDisable(false)
        setSubmitting(false)
        return false
      }
    } catch (error) {
      LogError(`Unable to save assessment`, error)
      userCtx.setNotificationError("Unable to save assessment")
      setDisable(false)
      setSubmitting(false)
      return false
    }
  }

  return (
    <Dialog
      header={`Assessment${assessment ? `: ${assessment.assessmentName}` : ""}`}
      visible={showForm}
      onHide={onCancel}
      className={myStyles.dialog}
    >
      {!answers ? <LoaderMedium /> : null}
      {submitting ? (
        <div className={myStyles.submitLayout}>
          <div className={myStyles.submitLayoutDescription}>
            Submitting assessment, please wait...
          </div>
          <div className={myStyles.submitLayoutLoader}>
            <LoaderMedium />
          </div>
        </div>
      ) : null}
      {answers && !submitting ? (
        <>
          <div
            className={classNames(
              page === 1 && !previewMode
                ? myStyles.outcomeOnlyLayout
                : myStyles.mainLayout,
              {
                [myStyles.mainLayoutFormOnly]:
                  assessment.completedAssessmentId &&
                  !(readOnly || previewMode) &&
                  page === 0,
              },
            )}
          >
            {assessment.instruction ? (
              <div className={myStyles.mainLayoutInstruction}>
                <AssessmentFormInstruction
                  instruction={assessment.instruction}
                />
              </div>
            ) : null}
            {assessment.completedAssessmentId &&
            (readOnly || (page === 1 && previewMode)) ? (
              <>
                <div className={myStyles.mainLayoutOutcomesHeader}>Overall</div>
                <div className={myStyles.mainLayoutFormHeader}>Assessment</div>
              </>
            ) : null}
            {page === 1 || displayCompletedOutcomes ? (
              <div className={myStyles.mainLayoutOutcomes}>
                <AssessmentFormOutcome
                  score={assessment?.score || 0}
                  outcomes={assessment?.suggestedOutcomes}
                  outcomesSelected={finalOutcomes}
                  remarksValue={finalRemarks}
                  previewMode={previewMode}
                  onCancel={(finalOutcomes, remarks) => {
                    setPage(0)

                    if (!previewMode) {
                      setFinalOutcomes(finalOutcomes || [])
                      setFinalRemarks(remarks || "")
                    }
                  }}
                  onSubmit={async (finalOutcomes, remarks) => {
                    if (!previewMode) {
                      await handleAssessmentSubmit(
                        answers,
                        finalOutcomes,
                        remarks,
                        true,
                      )
                    } else {
                      onCancel()
                    }
                  }}
                  disable={disable || readOnly}
                  completedAssessment={
                    assessment.completedAssessmentId ? assessment : undefined
                  }
                  validateScore={assessment.validateScore}
                  minScore={assessment.minScore}
                  maxScore={assessment.maxScore}
                />
              </div>
            ) : null}

            {page === 0 ||
            (assessment.completedAssessmentId && (readOnly || previewMode)) ? (
              <div className={myStyles.mainLayoutForm}>
                <AssessmentFormQuestionContainer
                  questions={assessment?.questions}
                  answers={answers}
                  scores={assessment?.scores}
                  alwaysAllowSubmit={isUserAnswers}
                  onSubmitValues={async (answers) => {
                    if (page === 0) {
                      setAnswers(answers)
                      setIsUserAnswers(true)

                      const result = await handleAssessmentSubmit(
                        answers,
                        [],
                        "",
                        false,
                      )

                      if (result) {
                        setPage(1)
                      }
                    } else {
                      onCancel()
                    }
                  }}
                  onCancel={() => {
                    if (previewMode && page === 1) {
                      setPage(0)
                    } else {
                      onCancel()
                    }
                  }}
                  readOnly={readOnly || (page !== 0 && previewMode)} //If preview mode, only allow editing on page 0
                  previewMode={page !== 0 && previewMode} //The form should only enter preview mode on page 1
                  completedAssessment={completedAssessmentId}
                  displayQuestions={assessment?.displayQuestions}
                  displayAnswerValues={assessment?.displayAnswerValues}
                  displayAnswers={assessment?.displayAnswers}
                  displayScore={
                    assessment?.completedAssessmentId
                      ? assessment?.displayScore &&
                        (readOnly || (page === 1 && previewMode))
                      : false
                  }
                />
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </Dialog>
  )
}
